import React, {useState, useRef, forwardRef, useEffect, useImperativeHandle} from 'react';
import Logo from '../assets/img/header/gohost_header_logo1.png';
import {motion} from 'framer-motion'
import { staggerContainer, fadeIn } from '../variants';
import Chops from '../assets/img/header/choppywoppy1.png';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { restaurantID } from '../App';



//header variants
const headerVariants = {
  hidden: {
    padding: '84px 0 84px 0',
    backgroundImage: 'none',
    backdropFilter: 'none',
  },
  show: {
    padding: '2px 0 0px 0',
    // Comment out the old awning background:
    // backgroundImage: `url(${Awning})`,
    // backgroundSize: 'auto 100%',
    // backgroundPosition: 'top',
    // backgroundRepeat: 'repeat-x',

    // New glassmorphism style with a dark background:
    backgroundColor: 'rgba(39,39,42,0.8)', // roughly equivalent to Tailwind's neutral-800 with 80% opacity
    backdropFilter: 'blur(10px)', // applies the blur effect
    transition: {
      type: 'spring',
      stiffness: 100,
    },
  },
};



export const navVariants = {
  hidden: {
    clipPath: 'circle(5.8% at 50% 0)',
    opacity: 0,
    transition: {
      type: 'spring',
      delay: 0.2,
      stiffness: 300,
      damping: 140,
    },
  },
  show: {
    opacity: 1, 
    clipPath: 'circle(130% at 50% 0)',
    transition: {
      type: 'spring',
      stiffness: 80,
    },
  },
};

const Header = forwardRef((props, ref) => {
  const {
    scrollToRef,
    refs,
    onMyAccountClick,
  } = props;
  const [isActive, setIsActive] = useState(false);
  const [nav, setNav] = useState(false);
  const [featureFlags, setFeatureFlags] = useState({
    allowWebReservations: true,
    allowOnlineOrders: true,
    allowNewGiftCards: true,
  });
  const navRef = useRef(null);


  useEffect(() => {
    const handleScroll = () => {
      setIsActive(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // This function is directly used in the onClick handler of each list item
  const closeNavAndScrollToRef = (ref) => {
    scrollToRef(ref);
    setNav(false); // Close the nav after scrolling
  };

  useEffect(() => {
    const fetchRestaurantFlags = async () => {
      try {
        const snap = await getDoc(
          doc(db, 'restaurants_data', restaurantID)
        );
  
        if (snap.exists()) {
          const data = snap.data();
          setFeatureFlags({
            allowWebReservations: data.allowWebReservations !== false,
            allowOnlineOrders:    data.allowOnlineOrders !== false,
            allowNewGiftCards:    data.allowNewGiftCards !== false,
          });
        }
      } catch (err) {
        console.error('Failed to load restaurant feature flags', err);
      }
    };
  
    fetchRestaurantFlags();
  }, []);

  // Load GoHost widgets once on mount.
  // my-reservation handles deep-link (?reservation=<id>) via s.onload since
  // document.currentScript is null for dynamically injected scripts.
  useEffect(() => {
    [
      { id: 'gh-my-res-script',       src: 'https://gohost-b65d2.web.app/widgets/my-reservation.js' },
      { id: 'gh-order-online-script', src: 'https://gohost-b65d2.web.app/widgets/order-online.js' },
      { id: 'gh-gift-card-script',    src: 'https://gohost-b65d2.web.app/widgets/gift-card.js' },
    ].forEach(({ id, src }) => {
      if (document.getElementById(id)) return;
      const s = document.createElement('script');
      s.id  = id;
      s.src = src;
      s.setAttribute('data-restaurant-id', restaurantID);
      if (id === 'gh-my-res-script') {
        s.onload = () => {
          const params = new URLSearchParams(window.location.search);
          const reservationId = params.get('reservation');
          if (reservationId && window.GoHostMyReservation) {
            window.GoHostMyReservation.open({ restaurantId: restaurantID, reservationId });
          }
        };
      }
      document.body.appendChild(s);
    });
  }, []);

  useEffect(() => {
    if (!nav) return;
  
    const handleClickOutside = (event) => {
      const navEl = navRef.current;
      const buttonEl = menuButtonRef.current;
  
      if (
        navEl &&
        !navEl.contains(event.target) &&
        buttonEl &&
        !buttonEl.contains(event.target)
      ) {
        setNav(false);
      }
    };
  
    document.addEventListener('mousedown', handleClickOutside);
  
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [nav]);
  

  const menuButtonRef = useRef(null);

  useImperativeHandle(ref, () => ({
    getMenuButtonPosition: () => {
      const rect = menuButtonRef.current?.getBoundingClientRect();
      if (!rect) return null;
      return {
        top: rect.top, // no scrollY
        left: rect.left + window.scrollX,
        width: rect.width,
        height: rect.height,
      };
    },
  }));
  
  const navItems = [
    { label: 'Top', action: () => closeNavAndScrollToRef(refs.heroRef) },
    { label: 'About', action: () => closeNavAndScrollToRef(refs.aboutRef) },
  
    featureFlags.allowWebReservations && {
      label: 'Make Reservation',
      action: () => closeNavAndScrollToRef(refs.tablesRef),
    },
  
    { label: 'Features and Pricing', action: () => closeNavAndScrollToRef(refs.menuRef) },
    { label: 'Story', action: () => closeNavAndScrollToRef(refs.teamRef) },
    { label: 'Contact', action: () => closeNavAndScrollToRef(refs.footerRef) },
  
    {
      label: 'My Reservation',
      action: () => {
        setNav(false);
        window.GoHostMyReservation?.open({ restaurantId: restaurantID });
      },
    },
  
    featureFlags.allowOnlineOrders && {
      label: 'Order Pickup',
      action: () => {
        setNav(false);
        window.GoHostOrderOnline?.open({ restaurantId: restaurantID });
      },
    },

    featureFlags.allowNewGiftCards && {
      label: 'Gift Cards',
      action: () => {
        setNav(false);
        window.GoHostGiftCard?.open({ restaurantId: restaurantID });
      },
    },

    {},
  ].filter(Boolean);

  const itemCount = navItems.length;

/**
 * Clamp gap size based on item count
 * More items → tighter spacing
 */
const gapClass =
  itemCount >= 9 ? 'gap-y-2'
  : itemCount >= 7 ? 'gap-y-3'
  : 'gap-y-5';
  

  return (
    <motion.div
    ref={navRef}
    variants={headerVariants}
    initial="hidden"
    animate={isActive ? 'show' : 'hidden'}
    className="select-none opacity-90 fixed w-full z-40 overflow-visible backdrop-blur-md"
  >
          <motion.div
              variants={staggerContainer}
              initial='hidden'
              animate={'show'}
              className="container mx-auto">
              <div className='flex justify-between items-center px-4 lg:px-0 relative text-white overflow-visible min-h-[80px]'>
                  <motion.div
                      ref={menuButtonRef}
                      variants={fadeIn('down', 'tween', 1.2, 1.4)}
                      className={`${nav ? 'gap-y-0' : 'gap-y-2'}
                      flex flex-col items-center justify-center w-12 h-12 z-50
                      p-3 order-3 lg:order-none cursor-pointer border-2 lg:border-[#f9f9e1]
                      rounded-full hover:bg-gradient-to-b from-red-800 to-[#d7c8a9] opacity-80 overflow-visible`}
                      onClick={() => setNav(!nav)}>
                      {/* Menu Icon */}
                     
  {/* menu blur ball? */}
{/* <div className='lg:opacity-90 sm:opacity-0'>
      <div className='z-1 '>   
        <div style={{ pointerEvents: 'none' }} className='sm:opacity-0 animate-pulse absolute blur-lg top-4 -left-1 w-[55px] h-[55px] bg-red-800 rounded-full mix-blend-multiply'>
        </div>
      </div>
      </div> */}

          {/* bar 1 */}
          <motion.img
              initial={{
                rotate: 0
              }}
              animate={{
                rotate: nav ? 45 : 0,
                translateY: nav ? 45 : 39,
                translateX: nav ? 1 : 0  // Adjust the horizontal position
              }}
              className='w-[90px] h-[150px] max-w-none max-h-none overflow-visible'
              src={Chops}>
            </motion.img>
            {/* bar 2 */}
            <motion.img
              initial={{
                rotate: 0
              }}
              animate={{
                rotate: nav ? -45 : 0,
                translateY: nav ? -45 : -46,
                translateX: nav ? 1 : 0  // Adjust the horizontal position
              }}
              className='w-[90px] h-[150px] max-w-none max-h-none overflow-visible'
              src={Chops}>
            </motion.img>
        </motion.div>


                   {/* logo — absolutely centered, only rendered once the header bar is active */}
        {isActive && (
          <motion.div
            className="absolute left-1/2 -translate-x-1/2"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1.4, ease: 'easeInOut' }}
          >
            <a href='#'>
              <img
                className="w-[80px] h-[80px] lg:w-[100px] lg:h-[100px] -mb-4"
                src={Logo}
                alt=''
              />
            </a>
          </motion.div>
        )}
        {/* social icons */}
        <motion.div
        variants={fadeIn('down', 'tween', 1.2, 1.4)}
        className='flex order-1 lg:order-none'>
          {/* My Account Button */}
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={onMyAccountClick}
            className="px-3 sm:px-8 lg:ml-16 py-2 border border-[#f9f9e1] rounded-full hover:bg-gradient-to-b from-red-800 to-[#d7c8a9] text-[#f9f9e1] text-sm transition duration-300 whitespace-nowrap"
          >
            My Account
          </motion.button>
        </motion.div>
        {/* nav button*/}
        <motion.div
    variants={navVariants}
    initial='hidden'
    animate={nav ? 'show' : ''}
    className='absolute bg-gradient-to-t from-yellow-600 to-red-800 w-[310px] h-[60vh] right-0 lg:left-0 top-[120px] bottom-0 z-50 rounded-lg p-4 border border-neutral-800'
>
<ul
  className={`h-full flex flex-col justify-center items-center ${gapClass} overflow-y-auto overscroll-contain`}
  style={{ WebkitOverflowScrolling: 'touch' }}
>
{navItems.map((item, idx) => (
  <li
    key={idx}
    className="cursor-pointer text-2xl capitalize text-[#f9f9e1] font-primary italic hover:text-[#30302b] transition-all duration-900"
    onClick={item.action}
  >
    {item.label}
  </li>
))}

</ul>

</motion.div>
      </div>

    </motion.div>

    </motion.div>
  
  );
});
export default React.memo(Header);